import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './ReopenQueue.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Contractors asking for settled tickets back.
 *
 * A ticket whose hours have been signed off is read-only for the contractor,
 * which is right - approved hours are what the firm bills on - but it means a
 * contractor who finds their own error has to come through somebody. This is
 * that queue.
 *
 * The one thing this component must get right is telling the approver the SIZE
 * of the decision before they make it. Granting a reopen on an invoiced ticket
 * does not touch only that ticket: the invoice is withdrawn, and every other
 * ticket on it goes back to the billing queue to be reissued under a new
 * number. Those tickets are not reopened and their hours do not change - but
 * somebody is going to have to raise that invoice again, and an approver who
 * finds that out afterwards has been ambushed by a button.
 */
export default function ReopenQueue() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_BASE}/reopen-requests?state=pending`);
      setRows(r.data || []);
      setDenied(false);
      setError(null);
    } catch (err) {
      // Not an error for somebody this queue was never for - it is simply not
      // theirs, and an error message would send them looking for a fault.
      if (err.response?.status === 403) setDenied(true);
      else setError(err.response?.data?.error || 'Could not read the reopen queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (row, decision) => {
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/reopen-requests/${row.id}/decide`,
        { decision, note: note.trim() || undefined });
      setOpenId(null);
      setNote('');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not record that decision');
    } finally {
      setBusy(false);
    }
  };

  // Hidden rather than shown empty: a "0 waiting" card on a page that is not
  // yours reads as "nothing to do", which is a different and wrong answer.
  if (denied) return null;
  if (loading) return null;
  if (rows.length === 0) return null;

  const when = (d) => (d
    ? new Date(d).toLocaleDateString('en-GB', { timeZone: 'Europe/Malta', day: 'numeric', month: 'short' })
    : '');

  return (
    <section className="rq">
      <div className="rq-head">
        <h2 className="rq-title">Reopen requests</h2>
        <span className="rq-count">{rows.length}</span>
      </div>
      <p className="rq-lead">
        Work that has already been signed off, which the contractor says needs
        changing.
      </p>

      {error && <p className="rq-error" role="alert">{error}</p>}

      <ul className="rq-list">
        {rows.map((row) => (
          <li className="rq-item" key={row.id}>
            <div className="rq-row">
              <span className="rq-ticket">{row.ticket_number}</span>
              <span className="rq-job">{row.title}</span>
              <span className="rq-who">
                {row.requested_by_contractor_name || row.requested_by_name || 'Unknown'}
                <span className="rq-when"> · {when(row.requested_at)}</span>
              </span>
            </div>

            <p className="rq-reason">“{row.reason}”</p>

            {/* What granting it would move, said before the button is pressed
                rather than after. */}
            {row.current_invoice_number ? (
              <p className="rq-impact">
                This ticket is on invoice <strong>{row.current_invoice_number}</strong>.
                {row.others_on_invoice > 0 ? (
                  <>
                    {' '}Reopening it withdraws that invoice: the other{' '}
                    <strong>{row.others_on_invoice}</strong>{' '}
                    ticket{row.others_on_invoice === 1 ? '' : 's'} on it go back to
                    Ready to Bill with their hours unchanged, to be reissued under a
                    new number. They are not reopened.
                  </>
                ) : (
                  <> Reopening it withdraws that invoice. No other ticket is affected.</>
                )}
              </p>
            ) : (
              <p className="rq-impact rq-quiet">
                Not invoiced — only this ticket moves. The billing queue is
                otherwise untouched.
              </p>
            )}

            {openId === row.id ? (
              <div className="rq-decide">
                <label htmlFor={`rq-note-${row.id}`}>
                  A note for the contractor (optional)
                </label>
                <textarea
                  id={`rq-note-${row.id}`}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <div className="rq-buttons">
                  <button type="button" className="rq-grant" disabled={busy}
                          onClick={() => decide(row, 'approve')}>
                    Reopen the ticket
                  </button>
                  <button type="button" className="rq-decline" disabled={busy}
                          onClick={() => decide(row, 'decline')}>
                    Decline
                  </button>
                  <button type="button" className="rq-cancel" disabled={busy}
                          onClick={() => { setOpenId(null); setNote(''); }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" className="rq-open"
                      onClick={() => { setOpenId(row.id); setNote(''); }}>
                Decide
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
